// Per-store cart kept in localStorage (a per-viewer convenience; the server
// prices every order from the stored menu, so nothing here is trusted).
import type { CartLine } from './types';

const KEY = (slug: string) => `sf.cart.${slug}`;

export function loadCart(slug: string): CartLine[] {
  try {
    const raw = window.localStorage.getItem(KEY(slug));
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(arr) ? arr.filter(isLine) : [];
  } catch { return []; }
}
export function saveCart(slug: string, lines: CartLine[]): void {
  try { window.localStorage.setItem(KEY(slug), JSON.stringify(lines)); } catch { /* ignore */ }
}
function isLine(v: unknown): v is CartLine {
  const o = v as CartLine;
  return !!o && typeof o.item === 'string' && Number.isInteger(o.quantity) && o.quantity > 0;
}

export function addLine(lines: CartLine[], item: string, quantity = 1, notes?: string): CartLine[] {
  const key = item.trim().toLowerCase();
  const i = lines.findIndex((l) => l.item.trim().toLowerCase() === key && (l.notes || '') === (notes || ''));
  if (i >= 0) {
    const next = lines.slice();
    next[i] = { ...next[i], quantity: Math.min(99, next[i].quantity + quantity) };
    return next;
  }
  return [...lines, { item: item.trim(), quantity: Math.min(99, Math.max(1, quantity)), ...(notes ? { notes } : {}) }];
}
/**
 * Set a line's quantity (0 removes it). With `notes` given (use '' for "no
 * notes") only that exact line changes; without it every line for the item
 * changes — what a chat "set_quantity"/"remove_from_cart" by name means.
 */
export function setQuantity(lines: CartLine[], item: string, quantity: number, notes?: string): CartLine[] {
  const key = item.trim().toLowerCase();
  const hit = (l: CartLine) => l.item.trim().toLowerCase() === key && (notes === undefined || (l.notes || '') === notes);
  if (quantity <= 0) return lines.filter((l) => !hit(l));
  return lines.map((l) => (hit(l) ? { ...l, quantity: Math.min(99, quantity) } : l));
}
export function removeLine(lines: CartLine[], item: string, notes?: string): CartLine[] {
  return setQuantity(lines, item, 0, notes);
}
export function cartCount(lines: CartLine[]): number {
  return lines.reduce((n, l) => n + l.quantity, 0);
}
